import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mic2, Sliders, Music, Headphones, Camera, Disc3, Clock } from "lucide-react";

const services = [
  {
    id: "recording",
    icon: Mic2,
    title: "Recording Studio",
    description: "Our main recording studio features state-of-the-art equipment, premium microphones, and a professionally treated acoustic environment. Perfect for vocals, instruments, and full band sessions.",
    features: ["Professional condenser microphones", "Acoustic isolation booth", "Multi-track recording", "Comfortable creative space"],
    image: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/ftumapj7_DSC_0100.jpg",
    hours: "Mon-Thu: 5PM-12AM | Fri-Sun: 9AM-2AM"
  },
  {
    id: "dj",
    icon: Disc3,
    title: "Dream Makers Sessions (DJ Room)",
    description: "Practice your sets or record your mixes in our professional DJ room. Featuring industry-standard Pioneer equipment, perfect for DJs of all skill levels looking to hone their craft.",
    features: ["3x CDJ3000s & DJM A9 Mixer", "Free Water & Soft Drinks", "Fast WiFi", "Sennheiser HD 25 Headphones Included", "Film Your Set Content Sessions ($125 / 2 hrs)"],
    image: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/qmcgb958_DSC_2699.jpg",
    hours: "12PM-12AM, 7 days a week"
  },
  {
    id: "mixing",
    icon: Sliders,
    title: "Mixing & Mastering",
    description: "Our experienced engineer will take your raw recordings and transform them into polished, radio-ready tracks. We use industry-standard tools and decades of experience to ensure your music sounds its absolute best.",
    features: ["Professional mixing", "Mastering for streaming", "Stem mixing available", "Revision rounds included"],
    image: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/zk0j5u4w_most-popular-waves-plugins.png",
    hours: "By appointment"
  },
  {
    id: "production",
    icon: Music,
    title: "Custom Beat Making",
    description: "From custom beat creation to full song production, our team can help bring your musical ideas to life. We work across all genres including hip-hop, R&B, pop, electronic, and more.",
    features: ["Custom beat production", "Song arrangement", "Vocal production", "Sound design"],
    image: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/411tigt5_22bc84-a13-1257-582b-32fe2be424d5_Understanding_the_Basics_of_Ableton_Live_-_A_Virtual_Recording_Studio_%20%2811%29.png",
    hours: "By appointment"
  },
  {
    id: "production-suite",
    icon: Headphones,
    title: "Production Suite Hire",
    description: "Need a private space to work on your beats or productions? Our production suite comes fully equipped with everything you need to create. Perfect for producers who want to work independently.",
    features: ["DAW workstation", "MIDI controllers", "Studio monitors", "Plug in and play"],
    image: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/5pgfec4p_Untitled%20design-3.jpg",
    hours: "Mon-Thu: 5PM-12AM | Fri-Sun: 9AM-2AM"
  },
  {
    id: "photography",
    icon: Camera,
    title: "Artist Press Shots",
    description: "Professional press shots and promotional photography in our dedicated photo studio. Whether you need album artwork, social media content, or press materials, we've got you covered.",
    features: ["Professional lighting", "Various backdrops", "30 edited photos included", "Dry hire available"],
    image: "https://customer-assets.emergentagent.com/job_dreammakers-studio/artifacts/ue0pb4zp_DSC_0004.jpg",
    hours: "By appointment"
  }
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

export default function Services() {
  return (
    <div className="pt-20" data-testid="services-page">
      {/* Hero */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Our Services</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Professional Studio Services
            </h1>
            <p className="text-white/60 text-lg">
              From recording to release, we offer a complete suite of services to help artists 
              bring their creative vision to life at the highest level.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 bg-dream-surface">
        <div className="max-w-7xl mx-auto px-6 space-y-32">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              variants={fadeInUp}
              className={`grid md:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? "md:flex-row-reverse" : ""
              }`}
              data-testid={`service-${service.id}`}
            >
              <div className={index % 2 === 1 ? "md:order-2" : ""}>
                <div className="w-14 h-14 rounded-xl bg-dream-violet/10 flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-dream-violet" />
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
                  {service.title}
                </h2>
                <p className="text-white/60 mb-6">{service.description}</p>
                
                <ul className="space-y-3 mb-6">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3 text-white/80">
                      <div className="w-2 h-2 bg-dream-violet rounded-full flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <div className="flex items-center gap-2 text-sm text-white/50 mb-8">
                  <Clock className="w-4 h-4" />
                  {service.hours}
                </div>

                <Link to="/booking">
                  <Button className="bg-dream-violet text-white hover:bg-dream-violet-glow rounded-full px-8 py-6">
                    Book This Service
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>

              <div className={`relative ${index % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className={`w-full h-full ${service.id === 'photography' ? 'object-contain bg-gray-100' : 'object-cover'}`}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-dream-bg/50 to-transparent rounded-2xl" />
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-8">
              Get in touch with us and we'll help you find the perfect solution for your project.
            </p>
            <Link to="/contact">
              <Button 
                variant="outline"
                className="border-dream-border text-white hover:border-dream-violet hover:text-dream-violet rounded-full px-8 py-6 bg-transparent"
              >
                Contact Us
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
